import moment, { Moment } from "moment";
import React, { useEffect, useRef, useState } from "react";
import { getUserDateFormat } from "../utils/getUserDateFormat";

type Props = {
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  maxDate?: string;
  required?: boolean;
};

const CustomFormatDatePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  required = false,
}: Props) => {
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const normalizeMomentFormat = (fmt: string) =>
    fmt.replace(/yyyy/gi, "YYYY").replace(/dd/gi, "DD");

  const displayFormat = normalizeMomentFormat(getUserDateFormat());
  const [formattedValue, setFormattedValue] = useState("");
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Moment>(moment());
  const [showCalendar, setShowCalendar] = useState(false);

  const isValid = (val: string) =>
    moment(val, displayFormat, true).isValid();

  const toISO = (m: Moment) => m.format("YYYY-MM-DD");

  const fromISO = (val: string) => {
    const dateOnly = val?.split("T")[0]; // 🔥 important
    return moment(dateOnly, "YYYY-MM-DD", true).format(displayFormat);
  };

  const parse = (val?: string) =>
    val ? moment(val, "YYYY-MM-DD") : null;

  const isInRange = (day: Moment) => {
    const min = parse(minDate);
    const max = parse(maxDate);
    if (min && day.isBefore(min, "day")) return false;
    if (max && day.isAfter(max, "day")) return false;
    return true;
  };

  useEffect(() => {
    if (value) {
      const dateOnly = value?.split("T")[0];
      const m = moment(dateOnly, "YYYY-MM-DD", true);
      setFormattedValue(fromISO(value));
      setSelectedDate(m);
      setCurrentMonth(m.clone());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      if (!pickerRef.current?.contains(e.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    let cursor = input.selectionStart || 0;

    const digitsBeforeCursor = rawValue.slice(0, cursor).replace(/\D/g, "").length;

    let digits = rawValue.replace(/\D/g, "");

    if (digits.length > 8) {
      digits = digits.slice(0, 8);
    }

    const separator =
      displayFormat.match(/[^A-Za-z]/)?.[0] || "/";

    const parts = displayFormat.split(separator);
    const lengths = parts.map((p) => p.length);

    let formatted = "";
    let index = 0;

    for (let i = 0; i < lengths.length; i++) {
      const chunk = digits.slice(index, index + lengths[i]);

      formatted += chunk;

      index += chunk.length;

      if (
        i < lengths.length - 1 &&
        digits.length > index
      ) {
        formatted += separator;
      }
    }

    setFormattedValue(formatted);

    let newCursor = digitsBeforeCursor;

    const firstBreak = lengths[0];
    const secondBreak = lengths[0] + lengths[1];

    if (newCursor > firstBreak) newCursor += 1;
    if (newCursor > secondBreak) newCursor += 1;

    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(newCursor, newCursor);
    });

    if (formatted.length >= 5) {
      const temp = moment(formatted, displayFormat, false);
      if (temp.isValid()) {
        setCurrentMonth(temp);
      }
    }

    if (formatted.length === 10) {
      const parsed = moment(formatted, displayFormat, true);

      if (parsed.isValid() && isInRange(parsed)) {
        setSelectedDate(parsed);
        setCurrentMonth(parsed.clone());
        onChange(toISO(parsed));
      } else {
        setSelectedDate(null);
      }
    }

    if (formatted === "") {
      setSelectedDate(null);
      setCurrentMonth(moment());
      onChange("");
    }
  };

  const handleDayClick = (day: Moment) => {
    if (!isInRange(day)) return;

    setSelectedDate(day);
    setCurrentMonth(day);
    setFormattedValue(day.format(displayFormat));
    onChange(toISO(day));
    setShowCalendar(false);
  };

  const changeMonth = (offset: number) => {
    setCurrentMonth((prev) => prev.clone().add(offset, "month"));
  };

  const today = moment();

  const renderCells = () => {
    const start = currentMonth.clone().startOf("month").startOf("week");
    const day = start.clone();
    const rows = [];

    for (let i = 0; i < 6; i++) {
      const cols = [];

      for (let j = 0; j < 7; j++) {
        const clone = day.clone();

        const isSelected =
          selectedDate && clone.isSame(selectedDate, "day");

        const isToday = clone.isSame(today, "day");

        const disabled = !isInRange(clone);

        cols.push(
          <div
            key={clone.toString()}
            onClick={() => !disabled && handleDayClick(clone)}
            className={`
              p-1 text-center rounded cursor-pointer text-sm
              ${disabled ? "opacity-30 pointer-events-none" : ""}
              ${isSelected ? "bg-secondary text-white" : ""}
              ${isToday ? "border border-secondary font-semibold" : ""}
              ${!isSelected && !disabled
                ? "hover:bg-blue-100"
                : ""
              }
            `}
          >
            {clone.date()}
          </div>
        );

        day.add(1, "day");
      }

      rows.push(
        <div key={i} className="grid grid-cols-7">
          {cols}
        </div>
      );
    }

    return rows;
  };

  const years = Array.from({ length: 101 }, (_, i) => {
    return moment().year() - 50 + i;
  });

  return (
    <div ref={pickerRef} className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        placeholder={displayFormat}
        value={formattedValue}
        onChange={handleInputChange}
        onFocus={() => setShowCalendar(true)}
        maxLength={10}
        required={required}
        className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
      />

      {formattedValue.length === 10 && !isValid(formattedValue) && (
        <p className="text-xs text-red-500 mt-1">
          Invalid date ({displayFormat})
        </p>
      )}

      {showCalendar && (
        <div className="absolute mt-2 bg-white text-black border rounded shadow p-3 z-50 w-64">
          <div className="flex justify-between mb-2">
            <button type="button" onClick={() => changeMonth(-1)}>◀</button>

            <div className="flex gap-2">
              <select
                value={currentMonth.month()}
                onChange={(e) => {
                  const updated = currentMonth
                    .clone()
                    .month(Number(e.target.value));

                  setCurrentMonth(updated);
                }}
                className="border rounded px-2 py-1 text-sm"
              >
                {moment.months().map(
                  (month, index) => (
                    <option
                      key={month}
                      value={index}
                    >
                      {month}
                    </option>
                  )
                )}
              </select>

              <select
                value={currentMonth.year()}
                onChange={(e) => {
                  const updated = currentMonth
                    .clone()
                    .year(Number(e.target.value));

                  setCurrentMonth(updated);
                }}
                className="border rounded px-2 py-1 text-sm"
              >
                {years.map((year) => (
                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <button type="button" onClick={() => changeMonth(1)}>▶</button>
          </div>

          <div className="grid grid-cols-7 text-xs text-center mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => <div key={d}>{d}</div>)}
          </div>

          {renderCells()}
        </div>
      )}
    </div>
  );
};

export default CustomFormatDatePicker;
